// What this browser does before a secret leaves it (D43, D44), mirroring the CLI
// (crates/ocs-cli/src/commands/credentials/seal.rs): seal the value to the daemon's sealing
// key, and sign the request with the device's authentication key. The crate builds the
// envelope and the signing input; WebCrypto contributes the randomness and the ECDSA
// signature with the non-extractable key.
import { api, type Schemas } from "../api/client";
import { unwrap } from "../api/queries";
import { AUTH_ALG } from "../device/keys";
import { base64ToBytes, bytesToBase64, owned, randomBytes, utf8 } from "../proto/bytes";
import { messages } from "../i18n";
import type { Proto } from "../proto/index";

export type SignedBody = Schemas["SignedBody"];
export type Method = "POST" | "PUT" | "DELETE";

/** The linked device that signs: one signed device is enough (Israel, 2026-10-04). */
export interface Signer {
  proto: Proto;
  deviceId: string;
  /** The device's ECDSA P-256 authentication key (non-extractable). */
  key: CryptoKey;
}

/**
 * Seal `value` for `orgId`/`kind` to the key `GET /api/sealing-key` returns, after checking
 * the key matches its id; returns the base64 `sealed_value`. Wipes `value` afterwards.
 */
export async function sealValue(proto: Proto, orgId: string, kind: string, value: Uint8Array): Promise<string> {
  const ikm = randomBytes(32);
  try {
    const key = await unwrap(api.GET("/api/sealing-key"));
    const publicKey = base64ToBytes(key.public_key);
    if (proto.sealingKeyId(publicKey) !== key.key_id) {
      throw new Error(messages().secrets.forms.sealingKeyMismatch(key.key_id));
    }
    return bytesToBase64(proto.sealCredentialValue(publicKey, orgId, kind, ikm, value));
  } finally {
    ikm.fill(0);
    value.fill(0);
  }
}

/** Seal a plaintext string (the value, or the JSON of a multi-part secret). */
export function sealText(proto: Proto, orgId: string, kind: string, text: string): Promise<string> {
  return sealValue(proto, orgId, kind, utf8.encode(text));
}

/**
 * The signed body for `method path` carrying `payload`: the JSON text is signed and sent
 * byte for byte, with the method, path and timestamp (docs/07 "Signed requests").
 */
export async function signedBody(
  signer: Signer,
  method: Method,
  path: string,
  payload: object,
  timestampMs: number = Date.now(),
): Promise<SignedBody> {
  const text = JSON.stringify(payload);
  const input = signer.proto.signedRequestSigningInput(method, path, timestampMs, utf8.encode(text));
  const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, signer.key, owned(input));
  return {
    device_id: signer.deviceId,
    timestamp: timestampMs,
    alg: AUTH_ALG,
    signature: bytesToBase64(new Uint8Array(signature)),
    payload: text,
  };
}
